import React, { useEffect, useState } from "react";

import type { ProfileClinical } from "types/profile";

import useAppContext from "../../AppContext";
import t, { AsHtml } from "utils/translate";
import { loginUrl, useCreateAccount } from "utils/profile";
import {
  EMAIL,
  FIRST_NAME,
  LAST_NAME,
  MEDICAL,
  OPTIN,
  PASSWORD
} from "components/Profile/constants";
import useLoggedInDataHandler from "utils/useLoggedInDataHandler";
import AccountCreate from "components/Profile/AccountCreate";
import RoundButton from "components/RoundButton";
import { getClinicTabData } from "utils/getClinicTabData";

import styles from "./styles.module.scss";

const AccountCreateModal: React.FC = () => {
  const {
    profile,
    setProfile,
    authData,
    setAuthData
  } = useAppContext();
  const { loggedInAs } = authData || {};
  const { trigger: createAccountTrigger, error: createAccountError, isMutating: isCreatingAccount } =
    useCreateAccount();
  const { getLoggedInData } = useLoggedInDataHandler({
    authData,
    setAuthData
  });
  const [open, setOpen] = useState<boolean>(false);

  const isInvalid =
    !profile ||
    !profile[FIRST_NAME] ||
    !profile[LAST_NAME] ||
    !profile[MEDICAL][EMAIL] ||
    !profile[PASSWORD];

  const createAccount = async () => {
    if (isInvalid) {
      return;
    }

    const response = await createAccountTrigger({
      email: profile[MEDICAL][EMAIL],
      password: profile[PASSWORD],
      firstname: profile[FIRST_NAME],
      lastname: profile[LAST_NAME],
      optin: profile[OPTIN]
    });

    if (response?.data?.success && setProfile) {
      const modifiedProfile = { ...profile, [PASSWORD]: "" };
      setProfile(modifiedProfile);
      await getLoggedInData();

      setOpen(false);
    }
  };

  const updateProfile = (key, value, firstLevelKey = null) => {
    if (!profile || !setProfile) {
      return;
    }

    let modifiedProfile = {} as ProfileClinical;

    if (!firstLevelKey) {
      modifiedProfile = { ...profile, [key]: value };
    } else {
      modifiedProfile = {
        ...profile,
        [firstLevelKey]: {
          ...(typeof profile[firstLevelKey] === "object"
            ? profile[firstLevelKey]
            : {}),
          [key]: value
        }
      };
    }

    setProfile(modifiedProfile);
  };

  const handleLogin = e => {
    e.preventDefault();

    window.sessionStorage.setItem("attemptedLogin", "true");
    window.location.href = loginUrl(window.location.href);
  };

  useEffect(() => {
    // Do not open login modal from clinic tab test.
    const { isFromClinicTab } = getClinicTabData();

    if (isFromClinicTab) {
      return;
    }

    setOpen(!loggedInAs);
  }, [loggedInAs]);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.accountCreateModal}>
        <div className={styles.inner}>
          <AsHtml
            className={styles.header}
            value={t("JS_CREATE_ACCOUNT_HEADER")}
          />
          <AsHtml
            className={styles.description}
            value={t("JS_CREATE_ACCOUNT_DESCRIPTION")}
          />
          <AccountCreate
            onChange={updateProfile}
            createAccountError={createAccountError}
          />
          <AsHtml
            className={styles.footer}
            value={t("JS_CREATE_ACCOUNT_FOOTER")}
          />
          <RoundButton
            onClick={createAccount}
            isDisabled={isInvalid}
            pending={isCreatingAccount}
            className={styles.button}
          >
            {t("SIGN_UP")}
          </RoundButton>
          <div className={styles.authSection}>
            <AsHtml
              tagType="span"
              value={t("LOGIN_TEXT")}
              interpolations={{ loggedInAs }}
            />
            <a className={styles.authLink} href="#" onClick={handleLogin}>
              {t("LOGIN_LINK")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountCreateModal;
